import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, Tag, ArrowRight, Stamp } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { API, inr, fmtErr } from "../lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [useStamps, setUseStamps] = useState(false);
  const navigate = useNavigate();

  const stamps = user?.stamps || 0;
  const canRedeem = stamps >= 5;
  const loyalty = useStamps && canRedeem ? Math.round(subtotal * 0.4) : 0;
  const discount = coupon?.discount || 0;
  const shipping = subtotal - discount - loyalty >= 1499 || items.length === 0 ? 0 : 99;
  const total = subtotal - discount - loyalty + shipping;

  const applyCoupon = async () => {
    try {
      const r = await API.post("/coupons/validate", { code, subtotal });
      setCoupon(r.data);
      toast.success(`Coupon ${r.data.code} applied — ${inr(r.data.discount)} off`);
    } catch (e) {
      setCoupon(null);
      toast.error(fmtErr(e));
    }
  };

  if (items.length === 0)
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center" data-testid="empty-cart">
        <h1 className="font-display font-black text-4xl tracking-tight">Your bag is empty</h1>
        <p className="text-muted-foreground mt-3">Identity crisis? Fix it with something bold.</p>
        <Link to="/shop" data-testid="empty-cart-shop-btn" className="btn-gold inline-flex items-center gap-2 px-8 py-4 mt-8 font-display font-bold text-sm uppercase tracking-[0.2em]">
          Shop now <ArrowRight size={16} />
        </Link>
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="cart-page">
      <h1 className="font-display font-black text-4xl tracking-tighter mb-10">Your bag <span className="text-muted-foreground text-xl font-normal">({items.length})</span></h1>
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          {items.map((i) => (
            <div key={`${i.product_id}|${i.size}|${i.color}`} data-testid={`cart-item-${i.slug}`}
              className="flex gap-5 border border-[#262626] bg-ink-surface p-4">
              <Link to={`/product/${i.slug}`}><img src={i.image} alt={i.name} className="h-32 w-24 object-cover" loading="lazy" /></Link>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Link to={`/product/${i.slug}`} className="font-display font-bold hover:text-gold transition-colors">{i.name}</Link>
                    <p className="text-xs text-muted-foreground mt-1">Size {i.size} · {i.color}</p>
                  </div>
                  <button onClick={() => removeItem(i)} data-testid={`remove-${i.slug}`} className="text-muted-foreground hover:text-red-400 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center border border-[#262626]">
                    <button onClick={() => updateQty(i, i.qty - 1)} className="p-2 text-muted-foreground hover:text-gold" data-testid={`qty-minus-${i.slug}`}><Minus size={13} /></button>
                    <span className="w-8 text-center text-sm font-bold">{i.qty}</span>
                    <button onClick={() => updateQty(i, i.qty + 1)} className="p-2 text-muted-foreground hover:text-gold" data-testid={`qty-plus-${i.slug}`}><Plus size={13} /></button>
                  </div>
                  <p className="font-display font-bold text-gold">{inr(i.price * i.qty)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="border border-[#262626] bg-ink-surface p-6 h-fit lg:sticky lg:top-28" data-testid="cart-summary">
          <h2 className="font-display font-bold text-lg mb-6">Order summary</h2>
          <div className="flex gap-2 mb-6">
            <div className="relative flex-1">
              <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input data-testid="coupon-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Coupon code" className="w-full bg-ink border border-[#262626] pl-9 pr-3 py-2.5 text-sm outline-none focus:border-gold uppercase" />
            </div>
            <button onClick={applyCoupon} data-testid="apply-coupon-btn" className="border border-gold text-gold px-4 text-xs font-bold uppercase tracking-wider hover:bg-gold/10 transition-colors">
              Apply
            </button>
          </div>
          {coupon && (
            <p className="text-xs text-gold mb-4 flex items-center justify-between" data-testid="coupon-applied">
              {coupon.code} applied
              <button onClick={() => { setCoupon(null); setCode(""); }} className="text-muted-foreground hover:text-red-400" data-testid="remove-coupon-btn">Remove</button>
            </p>
          )}
          {user && (
            <div className="border border-gold/40 bg-ink p-4 mb-5" data-testid="stamp-card">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-[0.2em] text-gold font-bold">Stamp card</p>
                <p className="text-xs text-muted-foreground" data-testid="stamp-count">{Math.min(stamps, 5)}/5</p>
              </div>
              <div className="flex gap-1.5 mb-3">
                {Array(5).fill(0).map((_, i) => (
                  <div key={`stamp-${i}`} className={`h-6 flex-1 border flex items-center justify-center ${i < stamps ? "bg-gold border-gold" : "border-[#262626]"}`}>
                    {i < stamps && <Stamp size={12} className="text-ink" />}
                  </div>
                ))}
              </div>
              {canRedeem ? (
                <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="redeem-stamps-label">
                  <input type="checkbox" checked={useStamps} onChange={(e) => setUseStamps(e.target.checked)} className="accent-[#D4AF37]" data-testid="redeem-stamps-checkbox" />
                  Redeem 5 stamps for <b className="text-gold">40% off</b> this order
                </label>
              ) : (
                <p className="text-[11px] text-muted-foreground">Earn 1 stamp per order. 5 stamps = 40% off.</p>
              )}
            </div>
          )}
          {!user && <p className="text-[11px] text-muted-foreground mb-4">Login to collect stamps — 5 stamps = 40% off an order.</p>}
          <div className="space-y-3 text-sm border-t border-[#262626] pt-4">
            <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span data-testid="cart-subtotal">{inr(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-gold"><span>Discount</span><span>-{inr(discount)}</span></div>}
            {loyalty > 0 && <div className="flex justify-between text-gold"><span>Stamp card (40%)</span><span data-testid="loyalty-discount">-{inr(loyalty)}</span></div>}
            <div className="flex justify-between text-muted-foreground">
              <span>Shipping</span><span>{shipping === 0 ? <span className="text-gold">FREE</span> : inr(shipping)}</span>
            </div>
            <div className="flex justify-between font-display font-black text-lg pt-3 border-t border-[#262626]">
              <span>Total</span><span className="gold-text" data-testid="cart-total">{inr(total)}</span>
            </div>
            <p className="text-[10px] text-muted-foreground">5% GST included in prices</p>
          </div>
          <button onClick={() => navigate("/checkout", { state: { coupon, loyalty: useStamps && canRedeem } })} data-testid="checkout-btn"
            className="btn-gold w-full mt-6 py-4 font-display font-bold text-sm uppercase tracking-[0.2em]">
            Checkout
          </button>
          <p className="text-[11px] text-muted-foreground text-center mt-3">Guest checkout available · UPI, cards, COD</p>
        </div>
      </div>
    </div>
  );
}
